import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import DepartmentIcon from '@atlaskit/icon/core/department';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import TeamsAvatar from '@atlaskit/teams-avatar/teams-avatar';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { messages } from '../../messages';
import { KnowledgeCardHeader } from '../common/knowledge-card-header';
import { KnowledgeCardsIntentType } from '../knowledge-cards-intent-type';
import { useTeamsAppLinks } from '../use-teams-app-links';

const styles = cssMap({
	orgIcon: {
		paddingRight: token('space.050'),
		paddingLeft: token('space.050'),
	},
});

type TeamCardHeaderProps = {
	name: string;
	accountId?: string;
	email?: string | null;
	avatarUrl?: string | null;
	/**
	 * @deprecated pass accountId & allow the URL to be generated by the navigation library
	 */
	profileUrl?: string | null;
	teamId?: string;
};

export const TeamCardHeader = ({
	name,
	accountId,
	email,
	avatarUrl,
	teamId,
	profileUrl,
}: TeamCardHeaderProps): React.JSX.Element => {
	const { commonAttributes } = useAIAnswerContext();
	const { formatMessage } = useIntl();
	const { cloudId } = commonAttributes;
	const { profileHref, profileOnNavigate } = useTeamsAppLinks({
		type: KnowledgeCardsIntentType.TEAM,
		currentUserId: accountId ?? '',
		cloudId,
		teamId: teamId,
	});

	const media = (
		<Box>
			<TeamsAvatar size="xlarge" src={avatarUrl || undefined} name={name} />
		</Box>
	);

	const orgText = (
		<Inline alignBlock="center">
			<Box xcss={styles.orgIcon}>
				<DepartmentIcon label="" />
			</Box>
			<Text color="color.text.subtlest">
				{formatMessage(messages.knowledge_cards_team_subheader)}
			</Text>
		</Inline>
	);

	const handleLinkClick = (e: React.MouseEvent) => {
		if (fg('platform-adopt-teams-nav-config')) {
			profileOnNavigate(e);
		}
	};

	return (
		<KnowledgeCardHeader
			name={name}
			email={email}
			profileUrl={fg('platform-adopt-teams-nav-config') ? profileHref : profileUrl || ''}
			profileLinkText={formatMessage(messages.knowledge_cards_view_team)}
			media={media}
			orgText={orgText}
			onLinkClick={handleLinkClick}
			testId={'team-knowledge-card-header'}
		/>
	);
};
